import { Flex, Grid, IconButton, PencilSquareIcon, Text, Tooltip, UserAvatar } from 'components'
import { startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import { ILeadLookupConfig } from 'types'
import { useBreakpoint, useUser } from 'utils'
import { getLookupValuesCount } from '../../utils/lookupUtils'

interface Props {
  leadLookUpConfig: Partial<ILeadLookupConfig>
  disableEdit?: boolean
  onEdit?: () => void
}

export default function SmartColumnConfigViewer({ leadLookUpConfig, disableEdit, onEdit }: Props) {
  const breakpoint = useBreakpoint()
  const [valuesCount, setValuesCount] = useState<number>()
  const configCreator = useUser(leadLookUpConfig?.createdBy)
  const updated = new Date(leadLookUpConfig?.updated!).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  const inputProp = leadLookUpConfig?.inputProperties?.map(prop => startCase(prop)).join()
  const outputProps = leadLookUpConfig?.outputProperties
  const hasTitleOrInput = leadLookUpConfig?.title || inputProp

  useEffect(() => {
    const fetchValuesCount = async () => {
      if (leadLookUpConfig?.id) setValuesCount(await getLookupValuesCount(leadLookUpConfig.id))
    }
    fetchValuesCount()
  }, [leadLookUpConfig])

  const label = hasTitleOrInput
    ? outputProps?.length
      ? `${startCase(inputProp!)} to ${outputProps.map(prop => prop.name).join(', ')}`
      : `${startCase(inputProp!)}`
    : 'Draft Smart Column'

  return breakpoint > 3 ? (
    <Flex width="100%" justify="space-between" gap={0}>
      <Grid templateColumns="280px 225px 200px 100px" gap={3}>
        <Flex direction="column" justify="center" pl={3}>
          <Text textStyle="bodySmall" opacity={inputProp ? 1 : 0.5} as={inputProp ? 'p' : 'i'}>
            {leadLookUpConfig?.title || label}
          </Text>
          {leadLookUpConfig?.description && (
            <Text textStyle="bodyXSmall">{leadLookUpConfig?.description}</Text>
          )}
        </Flex>
        <Flex align="center">
          <UserAvatar user={configCreator} />
          <Text {...readOnlyTextProps}>{configCreator?.displayName}</Text>
        </Flex>
        <Text {...readOnlyTextProps}>{updated}</Text>
        <Text {...readOnlyTextProps}>{valuesCount?.toLocaleString()}</Text>
      </Grid>
      <Tooltip
        content={
          disableEdit
            ? 'This smart column is used as an input field for another smart column and cannot be modified until that dependency is removed.'
            : ''
        }
      >
        <IconButton
          aria-label="edit-config"
          icon={<PencilSquareIcon fontSize="sm" />}
          disabled={disableEdit}
          onClick={onEdit}
        />
      </Tooltip>
    </Flex>
  ) : (
    <Flex direction="column" gap={1} overflow="hidden">
      <Text textStyle="bodySmall">{leadLookUpConfig?.title || label}</Text>
      <Text textStyle="bodyXSmall" opacity={0.7}>
        {leadLookUpConfig?.description}
      </Text>
    </Flex>
  )
}

const readOnlyTextProps = {
  textStyle: 'bodySmall',
  truncate: true,
  p: 3,
}
